import { useEffect, useState, useMemo } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/modules";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Search, Mail, Phone, LogIn, LogOut, Cake, PartyPopper, MessageCircle, Trash2, BookUser, Briefcase, Hash, MapPin, Plane, CheckCircle2, Circle } from "lucide-react";
import { PageHeader } from "../components/GradientCard";
import { iconForUser, letterGlow as sharedLetterGlow } from "../lib/dailyIcon";

function GlowingLetter({ name, chosenIcon, size = 54 }) {
  const g = sharedLetterGlow(name);
  const Icon = iconForUser({ chosen: chosenIcon, name });
  return (
    <div
      className={`relative flex-shrink-0 rounded-2xl grid place-items-center ring-1 ring-slate-200/70 dark:ring-slate-700/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden`}
      style={{
        width: size,
        height: size,
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(10px) saturate(140%)",
        WebkitBackdropFilter: "blur(10px) saturate(140%)",
      }}
      data-keep-color
      aria-hidden="true"
    >
      {/* Faint colour wash so the tile picks up the seeded accent */}
      <span
        className="absolute inset-0 pointer-events-none"
        style={{ background: `radial-gradient(circle at 30% 20%, ${g.glow}, transparent 65%)` }}
        data-keep-color
      />
      {/* Frost highlight (top edge) */}
      <span
        className="absolute inset-x-2 top-1 h-3 rounded-full bg-white/50 blur-md pointer-events-none"
        data-keep-color
      />
      <Icon
        size={Math.round(size * 0.5)}
        className={`relative ${g.text.split(" ")[0]}`}
        style={{ filter: `drop-shadow(0 0 8px ${g.glow})` }}
      />
    </div>
  );
}

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return null; }
};

// Returns { daysUntil, isToday, years } for an MM-DD anniversary based on ISO date string.
function daysUntilAnniversary(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(today.getFullYear(), d.getMonth(), d.getDate());
  if (target < today) target.setFullYear(today.getFullYear() + 1);
  const diff = Math.floor((target - today) / (1000 * 60 * 60 * 24));
  const years = target.getFullYear() - d.getFullYear();
  return { daysUntil: diff, isToday: diff === 0, years };
}

export default function Directory() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [people, setPeople] = useState([]);
  const [punchMap, setPunchMap] = useState({});
  const [iconMap, setIconMap] = useState({});
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [postTarget, setPostTarget] = useState(null); // { person, occasion }
  const [postMessage, setPostMessage] = useState("");
  const [farewellTarget, setFarewellTarget] = useState(null); // { person, resignation_id }
  const [farewellMessages, setFarewellMessages] = useState([]);
  const [farewellInput, setFarewellInput] = useState("");
  const [farewellSending, setFarewellSending] = useState(false);

  const openFarewell = async (person) => {
    setFarewellTarget(person);
    setFarewellInput("");
    setFarewellMessages([]);
    try {
      const { data } = await api.get(`/resignations/${person.active_resignation.id}/farewell-messages`);
      setFarewellMessages(data || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  const submitFarewell = async () => {
    if (!farewellInput.trim() || !farewellTarget) return;
    setFarewellSending(true);
    try {
      await api.post(`/resignations/${farewellTarget.active_resignation.id}/farewell-messages`, { message: farewellInput.trim() });
      toast.success("Good wishes posted");
      setFarewellInput("");
      const { data } = await api.get(`/resignations/${farewellTarget.active_resignation.id}/farewell-messages`);
      setFarewellMessages(data || []);
    } catch (e) { toast.error(formatError(e)); } finally { setFarewellSending(false); }
  };

  const load = async () => {
    try {
      const [d, p, m, ic] = await Promise.all([
        api.get("/directory"),
        api.get("/punch-status-today"),
        api.get("/celebrations/messages"),
        api.get("/daily-icons/today"),
      ]);
      setPeople(d.data);
      setPunchMap(p.data);
      setMessages(m.data);
      setIconMap((ic.data && ic.data.icons) || {});
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const messagesByUser = useMemo(() => {
    const m = {};
    const todayKey = new Date().toISOString().slice(0, 10);
    messages.forEach((msg) => {
      const postedDay = (msg.posted_at || "").slice(0, 10);
      if (postedDay !== todayKey) return; // only show TODAY's messages on the cards
      if (!m[msg.user_id]) m[msg.user_id] = [];
      m[msg.user_id].push(msg);
    });
    return m;
  }, [messages]);

  const enriched = useMemo(() => people.map((p) => ({
    ...p,
    birthday: daysUntilAnniversary(p.date_of_birth),
    anniversary: daysUntilAnniversary(p.date_of_joining),
  })), [people]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    let list = enriched;
    if (s) list = list.filter((p) => [p.name, p.email, p.employee_code, p.phone, p.department, p.designation].some((v) => String(v ?? "").toLowerCase().includes(s)));
    // Sort: today's celebrants first, then by nearest upcoming celebration
    return [...list].sort((a, b) => {
      const aMin = Math.min(a.birthday?.daysUntil ?? 999, a.anniversary?.daysUntil ?? 999);
      const bMin = Math.min(b.birthday?.daysUntil ?? 999, b.anniversary?.daysUntil ?? 999);
      return aMin - bMin;
    });
  }, [enriched, q]);

  const todayCelebrants = useMemo(() => filtered.filter((p) => p.birthday?.isToday || p.anniversary?.isToday), [filtered]);

  const submitMessage = async () => {
    if (!postMessage.trim()) { toast.error("Write a message"); return; }
    try {
      await api.post("/celebrations/messages", {
        user_id: postTarget.person.id, occasion: postTarget.occasion, message: postMessage.trim(),
      });
      toast.success("Message posted — visible to everyone");
      setPostTarget(null); setPostMessage(""); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const [pendingDeleteMsg, setPendingDeleteMsg] = useState(null);
  const deleteMessage = (mid) => setPendingDeleteMsg(mid);
  const confirmDeleteMessage = async (reason) => {
    try {
      await api.delete(`/celebrations/messages/${pendingDeleteMsg}`, { data: { reason } });
      toast.success("Moved to Recycle Bin");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  return (
    <div className="max-w-[1400px] space-y-6">
      <DirectoryHero people={filtered} todayCelebrants={todayCelebrants} />
      <WelcomeBanner />

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, email, department…" className="pl-9 h-10" data-testid="directory-search" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-slate-400 text-sm">No employees found.</div>}
        {filtered.map((p) => {
          const punch = punchMap[p.id];
          const inTime = fmtTime(punch?.check_in);
          const outTime = fmtTime(punch?.check_out);
          const bdayToday = p.birthday?.isToday;
          const annivToday = p.anniversary?.isToday;
          const todayMsgs = messagesByUser[p.id] || [];
          const whatsappNumber = (p.phone || "").replace(/\D/g, "");
          return (
            <div
              key={p.id}
              className={`group pk-depth ${bdayToday ? "pk-depth-rose" : annivToday ? "pk-depth-amber" : "pk-depth-indigo"} relative overflow-hidden rounded-xl p-5`}
              data-testid={`directory-card-${p.email}`}
            >
              {/* corner accent blob */}
              <span className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-indigo-400 to-violet-600 opacity-[0.08] blur-2xl group-hover:opacity-20 transition-opacity" aria-hidden="true" />

              <div className="relative flex items-start gap-3">
                <GlowingLetter name={p.name} chosenIcon={iconMap[p.id]} size={54} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-slate-900 truncate">{p.name}</div>
                    <StatusBadge status={p.today_status} label={p.today_status_label} testid={`directory-punch-${p.email}`} />
                  </div>
                  <div className="text-[12px] text-slate-600 truncate flex items-center gap-1.5 mt-0.5"><Briefcase size={11} className="text-indigo-500" /> {p.designation || ROLE_LABELS[p.role] || p.role}</div>
                  {p.today_location && p.today_status !== "on_leave" && (
                    <div className="text-[10.5px] text-slate-500 mt-0.5 flex items-center gap-1"><MapPin size={10} /> {p.today_location}</div>
                  )}
                  {p.department && (
                    <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{p.department}</span>
                  )}
                </div>
              </div>

              {/* Celebration badges */}
              {(p.birthday || p.anniversary) && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`directory-celebs-${p.email}`}>
                  {p.birthday && (
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${bdayToday ? "bg-pink-600 text-white shadow-sm shadow-pink-300/50" : p.birthday.daysUntil <= 30 ? "bg-pink-100 text-pink-700 border border-pink-200" : "bg-slate-100 text-slate-500"}`} data-testid={`directory-bday-${p.email}`}>
                      <Cake size={9} /> {bdayToday ? "Birthday today!" : `Birthday in ${p.birthday.daysUntil}d`}
                    </span>
                  )}
                  {p.anniversary && (
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${annivToday ? "bg-amber-600 text-white shadow-sm shadow-amber-300/50" : p.anniversary.daysUntil <= 30 ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-slate-100 text-slate-500"}`} data-testid={`directory-anniv-${p.email}`}>
                      <PartyPopper size={9} /> {annivToday ? `${p.anniversary.years} yr work anniversary today!` : `Work anniv in ${p.anniversary.daysUntil}d`}
                    </span>
                  )}
                </div>
              )}

              {/* Posted messages */}
              {todayMsgs.length > 0 && (
                <div className="mt-3 space-y-1.5" data-testid={`directory-msgs-${p.email}`}>
                  {todayMsgs.map((m) => (
                    <div key={m.id} className="border-l-2 border-pink-400 bg-pink-50/60 rounded-r-md pl-2 pr-1 py-1 text-[11px]">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-slate-700">{m.message}</span>
                        {isMaster && (
                          <button onClick={() => deleteMessage(m.id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`directory-msg-delete-${m.id}`}><Trash2 size={11} /></button>
                        )}
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5">— {m.posted_by_name}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Master Admin post message button — only when celebration today */}
              {isMaster && (bdayToday || annivToday) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setPostTarget({ person: p, occasion: bdayToday ? "birthday" : "anniversary" }); setPostMessage(""); }}
                  className="mt-3 h-8 text-xs w-full"
                  data-testid={`directory-post-message-${p.email}`}
                >
                  <MessageCircle size={11} className="mr-1.5" /> Post a Special Message
                </Button>
              )}

              {(inTime || outTime) && (
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`directory-punch-times-${p.email}`}>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-semibold mono">
                    <LogIn size={11} /> {inTime || "—"}
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 text-slate-600 text-[11px] font-semibold mono">
                    <LogOut size={11} /> {outTime || "—"}
                  </div>
                </div>
              )}

              {/* Contact strip — interactive chips */}
              <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                <a
                  href={`mailto:${p.email}`}
                  className="flex items-center gap-2 text-[12px] text-slate-700 hover:text-indigo-700 transition-colors group/email"
                  data-testid={`directory-email-${p.email}`}
                >
                  <span className="w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 grid place-items-center group-hover/email:bg-indigo-100 transition-colors">
                    <Mail size={11} strokeWidth={2} />
                  </span>
                  <span className="mono truncate flex-1">{p.email}</span>
                </a>
                <div className="flex items-center gap-1.5">
                  <a
                    href={p.phone ? `tel:${p.phone}` : "#"}
                    className={`flex items-center gap-2 text-[12px] text-slate-700 hover:text-emerald-700 transition-colors group/phone flex-1 min-w-0 ${!p.phone ? "pointer-events-none opacity-50" : ""}`}
                    data-testid={`directory-phone-${p.email}`}
                  >
                    <span className="w-6 h-6 rounded-md bg-emerald-50 text-emerald-600 grid place-items-center group-hover/phone:bg-emerald-100 transition-colors">
                      <Phone size={11} strokeWidth={2} />
                    </span>
                    <span className="mono truncate flex-1">{p.phone || "—"}</span>
                  </a>
                  {whatsappNumber && (
                    <a
                      href={`https://wa.me/${whatsappNumber.startsWith("91") ? whatsappNumber : "91" + whatsappNumber}`}
                      target="_blank"
                      rel="noreferrer"
                      className="w-7 h-7 rounded-md bg-green-50 text-green-600 grid place-items-center border border-green-200 hover:bg-green-500 hover:text-white transition-all hover:-translate-y-0.5 shrink-0"
                      title="Message on WhatsApp"
                      data-testid={`directory-whatsapp-${p.email}`}
                    >
                      <MessageCircle size={12} strokeWidth={2} />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <Hash size={10} className="text-slate-400" />
                  <span className="mono uppercase tracking-wider">Code · {p.employee_code || "—"}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!postTarget} onOpenChange={(o) => !o && setPostTarget(null)}>
        <DialogContent className="max-w-md" data-testid="post-message-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {postTarget?.occasion === "birthday" ? "🎂" : "🎉"} Message for {postTarget?.person?.name}
            </DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">Visible to everyone for today.</div>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
              {postTarget?.occasion === "birthday" ? "Birthday Wish" : "Work Anniversary Note"}
            </Label>
            <Textarea rows={3} value={postMessage} onChange={(e) => setPostMessage(e.target.value)} placeholder="Write something heartfelt…" className="mt-1.5" data-testid="post-message-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostTarget(null)}>Cancel</Button>
            <Button onClick={submitMessage} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="post-message-submit">Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteWithReasonDialog
        open={!!pendingDeleteMsg}
        onClose={() => setPendingDeleteMsg(null)}
        title="Delete this message?"
        description="It will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteMessage}
        testid="directory-msg-delete"
      />
    </div>
  );
}


function StatusBadge({ status, label, testid }) {
  const map = {
    on_leave:    { cls: "bg-amber-500 text-white border-amber-600 shadow-sm shadow-amber-200/40",   icon: Plane },
    punched_in:  { cls: "bg-emerald-500 text-white border-emerald-600 shadow-sm shadow-emerald-200/40", icon: CheckCircle2 },
    punched_out: { cls: "bg-slate-400 text-white border-slate-500",                                  icon: CheckCircle2 },
  };
  const m = map[status] || { cls: "bg-rose-500 text-white border-rose-600 shadow-sm shadow-rose-200/40", icon: Circle };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.cls}`} data-testid={testid}>
      <Icon size={10} strokeWidth={2.4} /> {label}
    </span>
  );
}

function WelcomeBanner() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/welcome-banner").then(({ data }) => setRows(data || [])).catch(() => {});
  }, []);
  if (!rows.length) return null;
  return (
    <div className="pk-card p-5 bg-gradient-to-r from-emerald-50 via-amber-50 to-violet-50 border-emerald-200" data-testid="welcome-banner">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">🌟 Welcome to the Family!</div>
      <div className="heading text-lg font-bold text-slate-900 mt-1">"Every great journey begins with a single step."</div>
      <div className="text-[12.5px] text-slate-700 mt-1">We are excited to be part of your professional journey and future success.</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {rows.map((p) => (
          <span key={p.id} className="text-sm bg-white border border-emerald-200 rounded-full px-3 py-1 font-semibold text-slate-800" data-testid={`welcome-pill-${p.id}`}>
            🌟 {p.name}{p.designation ? ` · ${p.designation}` : ""}{p.company ? ` @ ${p.company}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// DirectoryHero — emotional, time-aware welcome strip showing total team
// size, who's celebrating today (with avatars), live punched-in count, and
// a rotating people-first quote.
// ---------------------------------------------------------------------------
const TEAM_QUOTES = [
  { q: "Alone we can do so little; together we can do so much.", a: "Helen Keller" },
  { q: "Coming together is a beginning; staying together is progress; working together is success.", a: "Henry Ford" },
  { q: "Talent wins games, but teamwork and intelligence win championships.", a: "Michael Jordan" },
  { q: "If you want to go fast, go alone. If you want to go far, go together.", a: "African Proverb" },
  { q: "The strength of the team is each individual member. The strength of each member is the team.", a: "Phil Jackson" },
  { q: "Great things in business are never done by one person.", a: "Steve Jobs" },
  { q: "Behind every successful person there is a team of supportive ones.", a: "Anonymous" },
];

function DirectoryHero({ people, todayCelebrants }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : hour < 20 ? "Good evening" : "Hello";
  const dateStr = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  const quote = useMemo(() => {
    const dayKey = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    return TEAM_QUOTES[dayKey % TEAM_QUOTES.length];
  }, []);
  const punchedIn = (people || []).filter((p) => ["working", "punched_in", "in"].includes(String(p.today_status || "").toLowerCase())).length;
  const onLeave = (people || []).filter((p) => ["leave", "on_leave"].includes(String(p.today_status || "").toLowerCase())).length;
  const total = (people || []).length;

  return (
    <div className="relative pk-depth pk-depth-indigo rounded-2xl overflow-hidden">
      <div className="px-5 sm:px-7 py-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              <span>{dateStr}</span>
              <span className="text-slate-300">·</span>
              <span>Employee Directory</span>
            </div>
            <h1 className="text-[26px] sm:text-[32px] font-extrabold tracking-tight text-slate-900 leading-[1.1]">
              {greeting}. <span className="text-indigo-600">Meet the team.</span>
            </h1>
            <div className="mt-3 inline-flex items-start gap-2 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 max-w-xl">
              <span className="text-indigo-400 text-[18px] leading-none mt-0.5 font-serif">&ldquo;</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-slate-700 leading-snug italic">{quote.q}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider font-semibold">&mdash; {quote.a}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 shrink-0">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[100px]">
              <div className="text-[9.5px] uppercase tracking-[0.15em] font-semibold text-slate-500">Team Size</div>
              <div className="text-[22px] font-extrabold text-slate-900 mono leading-none mt-1">{total}</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 min-w-[100px]" data-keep-color>
              <div className="text-[9.5px] uppercase tracking-[0.15em] font-semibold text-emerald-700">In Office Now</div>
              <div className="text-[22px] font-extrabold text-emerald-700 mono leading-none mt-1 flex items-center gap-1.5">
                {punchedIn}
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 min-w-[100px]" data-keep-color>
              <div className="text-[9.5px] uppercase tracking-[0.15em] font-semibold text-amber-700">On Leave</div>
              <div className="text-[22px] font-extrabold text-amber-700 mono leading-none mt-1">{onLeave}</div>
            </div>
          </div>
        </div>

        {todayCelebrants.length > 0 && (
          <div className="mt-5 rounded-xl border border-pink-200 bg-pink-50/40 p-3.5" data-testid="celebrations-banner" data-keep-color>
            <div className="flex items-center gap-2 mb-2.5">
              <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-700 flex items-center justify-center">
                <PartyPopper size={14} />
              </div>
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-[0.15em] font-bold text-pink-700">Today&apos;s Celebrations</div>
                <div className="text-[12px] text-slate-600">Send them a warm message from their card below.</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {todayCelebrants.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1.5 text-[12px] bg-white border border-pink-200 rounded-full pl-1 pr-3 py-0.5 font-semibold text-slate-800" data-testid={`celebrant-pill-${p.email}`}>
                  <span className="w-6 h-6 rounded-full bg-pink-100 text-pink-700 grid place-items-center text-[10px] font-bold ring-1 ring-pink-200 overflow-hidden">
                    {p.name?.charAt(0).toUpperCase() || "?"}
                  </span>
                  {p.birthday?.isToday && <Cake size={11} className="text-pink-600" />}
                  {p.anniversary?.isToday && <PartyPopper size={11} className="text-amber-600" />}
                  {p.name}
                  {p.anniversary?.isToday && <span className="text-[10px] text-slate-500 font-normal">· {p.anniversary.years} yr</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

